import { useEffect, useRef } from 'react'

import { MIN_SEATS, maxSeatCount } from '../lib/geometry'
import { useT } from '../lib/i18n/I18nProvider'
import { usePlanStore } from '../store/planStore'
import { useViewStore } from '../store/viewStore'

/** True while the user is typing, so shortcuts never eat their keystrokes. */
function isEditingText(target: EventTarget | null): boolean {
  const element = target as HTMLElement | null
  if (!element) return false
  const tag = element.tagName
  return (
    tag === 'INPUT' ||
    tag === 'TEXTAREA' ||
    tag === 'SELECT' ||
    element.isContentEditable === true
  )
}

export function useHotkeys(enabled: boolean): void {
  // Read through a ref so a language switch never re-binds the listener.
  const t = useT()
  const tRef = useRef(t)
  tRef.current = t

  useEffect(() => {
    if (!enabled) return undefined

    const onKeyDown = (event: KeyboardEvent) => {
      const store = usePlanStore.getState()
      const mod = event.metaKey || event.ctrlKey

      if (mod && event.key.toLowerCase() === 'z') {
        if (isEditingText(event.target)) return // let the browser undo the text
        event.preventDefault()
        if (event.shiftKey) store.redo()
        else store.undo()
        return
      }

      if (mod && event.key.toLowerCase() === 'y') {
        if (isEditingText(event.target)) return
        event.preventDefault()
        store.redo()
        return
      }

      // ⌘A means "select all" everywhere else on a canvas; without it, taking
      // the whole room means Shift-dragging a box round it.
      if (mod && event.key.toLowerCase() === 'a') {
        if (isEditingText(event.target)) return // let the browser select the text
        const tables = store.plan?.tables ?? []
        if (tables.length === 0) return
        event.preventDefault()
        store.selectTables(tables.map((table) => table.id))
        return
      }

      // Everything below is a bare key. With ⌘/Ctrl held it belongs to the
      // browser — ⌘D bookmarks and Ctrl+/− zoom the page, and hijacking those
      // because a table happened to be selected is a bug, not a shortcut.
      if (mod) return

      if (isEditingText(event.target)) return

      switch (event.key) {
        case 'Delete':
        case 'Backspace': {
          if (store.activeSeat) {
            event.preventDefault()
            store.clearSeat(store.activeSeat)
            return
          }
          if (store.selectedTableIds.length > 0) {
            event.preventDefault()
            store.deleteTables(store.selectedTableIds)
          }
          return
        }

        case 'Escape': {
          const view = useViewStore.getState()
          // Leave the innermost thing first: a guest waiting for a chair.
          if (view.placing) {
            view.stopPlacing()
            return
          }
          store.clearSelection()
          view.endDrag()
          view.setSelectMode(false)
          return
        }

        case 'ArrowUp':
        case 'ArrowDown':
        case 'ArrowLeft':
        case 'ArrowRight': {
          if (store.selectedTableIds.length === 0) return
          event.preventDefault()
          const distance = event.shiftKey ? 40 : 10
          const dx = event.key === 'ArrowLeft' ? -distance : event.key === 'ArrowRight' ? distance : 0
          const dy = event.key === 'ArrowUp' ? -distance : event.key === 'ArrowDown' ? distance : 0
          store.nudgeTables(store.selectedTableIds, dx, dy)
          return
        }

        case 'd':
        case 'D': {
          if (store.selectedTableIds.length !== 1) return
          event.preventDefault()
          store.duplicateTable(store.selectedTableIds[0])
          return
        }

        case '=':
        case '+': {
          if (store.selectedTableIds.length !== 1) return
          event.preventDefault()
          // The store stays silent at the cap (it has no catalogue); here the
          // planner pressed a key and got nothing, so say why, in their language.
          const table = store.plan?.tables.find((entry) => entry.id === store.selectedTableIds[0])
          if (table && !store.addSeat(table.id)) {
            store.showToast(tRef.current.inspector.atMaxChairs(maxSeatCount(table.shape)), 'warn')
          }
          return
        }

        case '-':
        case '_': {
          if (store.selectedTableIds.length !== 1) return
          event.preventDefault()
          const table = store.plan?.tables.find((entry) => entry.id === store.selectedTableIds[0])
          if (table && !store.removeSeat(table.id) && table.seatCount <= MIN_SEATS) {
            store.showToast(tRef.current.inspector.atMinChairs, 'warn')
          }
          return
        }

        case 'g':
        case 'G':
          useViewStore.getState().toggleGrid()
          return

        default:
          break
      }
    }

    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [enabled])
}
